import { apiClient } from './client'

/**
 * LGPD Block C — privacy/consent backend client.
 *
 * Thin wrappers over the consent-log endpoints. They go through ``apiClient``,
 * so they inherit the BFF strategy automatically: ``credentials: 'include'``
 * (HttpOnly session cookie flows so the backend can attach ``user_id`` when the
 * user is logged in) and the ``X-CSRF-Token`` double-submit on the POSTs.
 *
 * These calls are **best-effort evidence logging**. The caller
 * (``consentBackend.ts``) swallows every rejection — the local consent record
 * (Block B) remains the single source of truth for UI gating, and a backend
 * failure must never break the app or load any script.
 */

/** Consent actions accepted by the backend (superset of the local Block B set). */
export type ConsentBackendAction =
  | 'accept_all'
  | 'reject_all'
  | 'save_preferences'
  | 'revoke'

/** Where the consent decision was made — mirrors the backend's VALID_SOURCES. */
export type ConsentBackendSource =
  | 'banner'
  | 'preferences'
  | 'invite'
  | 'login'
  | 'settings'

export interface PolicyCurrentDTO {
  policyVersion: string
  privacyPolicyUrl: string | null
  termsUrl: string | null
  betaDataPolicyUrl: string | null
  effectiveAt: string | null
}

export interface ConsentAckDTO {
  id: string
  action: string
  policyVersion: string
  createdAt: string | null
  stored: boolean
}

export interface ConsentStateDTO {
  action: string
  policyVersion: string
  categories: { essential: boolean; analytics: boolean; marketing: boolean }
  source: string | null
  createdAt: string | null
}

export interface ConsentMeDTO {
  consent: ConsentStateDTO | null
}

export interface ConsentEventPayload {
  policyVersion: string
  action: ConsentBackendAction
  categories: { essential: boolean; analytics: boolean; marketing: boolean }
  source?: ConsentBackendSource
  anonymousId?: string
}

export interface ConsentRevokePayload {
  policyVersion?: string
  source?: ConsentBackendSource
  anonymousId?: string
}

export const privacyApi = {
  /** Current published policy version + legal URLs. Public, no auth/CSRF. */
  getCurrentPolicy: (signal?: AbortSignal) =>
    apiClient.get<PolicyCurrentDTO>(
      '/api/v1/privacy/policy/current',
      undefined,
      signal,
    ),

  /**
   * Append a consent event. ``skipAuthRefresh`` because the endpoint is
   * pre-auth-safe (it never 401s) — an anonymous caller must not trigger a
   * spurious ``/auth/refresh`` attempt.
   */
  postConsent: (payload: ConsentEventPayload) =>
    apiClient.post<ConsentAckDTO>('/api/v1/privacy/consent', payload, {
      skipAuthRefresh: true,
    }),

  /** Latest consent state for the session user, or by ``anonymousId``. */
  getMyConsent: (anonymousId?: string, signal?: AbortSignal) =>
    apiClient.get<ConsentMeDTO>(
      '/api/v1/privacy/consent/me',
      anonymousId ? { anonymousId } : undefined,
      signal,
    ),

  /** Append a revoke event (never deletes prior evidence). */
  revokeConsent: (payload: ConsentRevokePayload) =>
    apiClient.post<ConsentAckDTO>('/api/v1/privacy/consent/revoke', payload, {
      skipAuthRefresh: true,
    }),
}
